import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { useT } from '../../i18n';

const VARIANT_TYPES = ['softcover', 'hardcover', 'digital'];

function emptyVariant() {
  return {
    sku: '',
    type: 'softcover',
    productKey: '',
    includesPdf: false,
    isActive: true,
  };
}

function TemplateEditor({ template, products, onCancel, onSaved }) {
  const t = useT();
  const [form, setForm] = useState(() => ({
    nameAr:        template.nameAr || '',
    slug:          template.slug || '',
    taglineAr:     template.taglineAr || '',
    descriptionAr: template.descriptionAr || '',
    coverImageUrl: template.coverImageUrl || '',
    isActive:      !!template.isActive,
    isFeatured:    !!template.isFeatured,
    sortOrder:     Number(template.sortOrder || 0),
    variants:      (template.variants || []).map(v => ({ ...v })),
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const setField = (key, value) => setForm(p => ({ ...p, [key]: value }));

  const setVariant = (i, key, value) => setForm(p => ({
    ...p,
    variants: p.variants.map((v, idx) => idx === i ? { ...v, [key]: value } : v),
  }));

  const removeVariant = (i) => setForm(p => ({
    ...p,
    variants: p.variants.filter((_, idx) => idx !== i),
  }));

  const addVariant = () => setForm(p => ({
    ...p,
    variants: [...p.variants, emptyVariant()],
  }));

  const save = async () => {
    setError('');
    if (form.variants.length > 0 && (!products || products.length === 0)) {
      setError(t.adminTemplateNeedProducts);
      return;
    }
    for (const v of form.variants) {
      if (!v.sku?.trim()) { setError(t.adminTemplateSkuRequired); return; }
      if (!v.productKey?.trim()) { setError(t.adminTemplateProductRequired); return; }
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        sortOrder: Number(form.sortOrder) || 0,
        variants: form.variants.map(v => ({
          sku: v.sku,
          type: v.type,
          productKey: v.productKey,
          includesPdf: !!v.includesPdf,
          isActive: !!v.isActive,
        })),
      };
      const updated = await api.patch(`/api/admin/templates/${template._id}`, payload);
      onSaved(updated);
    } catch (e) {
      setError(e.message || t.adminSaveFailed);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-editor">
      <div className="admin-editor-grid">
        <label className="admin-editor-field">
          <span>{t.adminTemplateName}</span>
          <input value={form.nameAr} onChange={e => setField('nameAr', e.target.value)} />
        </label>
        <label className="admin-editor-field">
          <span>Slug</span>
          <input className="admin-mono" value={form.slug} onChange={e => setField('slug', e.target.value)} />
        </label>
        <label className="admin-editor-field admin-editor-field-wide">
          <span>{t.adminTemplateTagline}</span>
          <input value={form.taglineAr} onChange={e => setField('taglineAr', e.target.value)} />
        </label>
        <label className="admin-editor-field admin-editor-field-wide">
          <span>{t.adminTemplateDescription}</span>
          <textarea rows={3} value={form.descriptionAr} onChange={e => setField('descriptionAr', e.target.value)} />
        </label>
        <label className="admin-editor-field admin-editor-field-wide">
          <span>{t.adminTemplateCoverUrl}</span>
          <input className="admin-mono" value={form.coverImageUrl} onChange={e => setField('coverImageUrl', e.target.value)} />
        </label>
        <label className="admin-editor-field">
          <span>{t.adminTemplateSortOrder}</span>
          <input type="number" value={form.sortOrder} onChange={e => setField('sortOrder', e.target.value)} />
        </label>
        <label className="admin-editor-field admin-editor-check">
          <input type="checkbox" checked={form.isActive} onChange={e => setField('isActive', e.target.checked)} />
          <span>{t.adminTemplateActive}</span>
        </label>
        <label className="admin-editor-field admin-editor-check">
          <input type="checkbox" checked={form.isFeatured} onChange={e => setField('isFeatured', e.target.checked)} />
          <span>{t.adminTemplateFeatured}</span>
        </label>
      </div>

      <div className="admin-variants">
        <div className="admin-variants-header">
          <h3>{t.adminTemplateVariantsTitle}</h3>
          <button type="button" className="admin-btn-secondary" onClick={addVariant}>{t.adminTemplateAddVariant}</button>
        </div>
        <table className="admin-variant-table">
          <thead>
            <tr>
              <th>SKU</th>
              <th>{t.adminTemplateVariantType}</th>
              <th>{t.adminTemplateVariantProduct}</th>
              <th>PDF</th>
              <th>{t.adminTemplateVariantEnabled}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {form.variants.map((v, i) => (
              <tr key={i}>
                <td>
                  <input className="admin-mono" value={v.sku || ''} onChange={e => setVariant(i, 'sku', e.target.value)} />
                </td>
                <td>
                  <select value={v.type} onChange={e => setVariant(i, 'type', e.target.value)}>
                    {VARIANT_TYPES.map(tp => <option key={tp} value={tp}>{t[tp] || tp}</option>)}
                  </select>
                </td>
                <td>
                  <select value={v.productKey || ''} onChange={e => setVariant(i, 'productKey', e.target.value)}>
                    <option value="">{t.adminTemplateSelectProduct}</option>
                    {(products || []).map(p => (
                      <option key={p.key} value={p.key}>
                        {p.nameAr} — {(p.priceCents / 100).toFixed(0)} {p.currency}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="admin-cell-center">
                  <input type="checkbox" checked={!!v.includesPdf} onChange={e => setVariant(i, 'includesPdf', e.target.checked)} />
                </td>
                <td className="admin-cell-center">
                  <input type="checkbox" checked={!!v.isActive} onChange={e => setVariant(i, 'isActive', e.target.checked)} />
                </td>
                <td>
                  <button type="button" className="admin-btn-danger" onClick={() => removeVariant(i)}>{t.adminDelete}</button>
                </td>
              </tr>
            ))}
            {form.variants.length === 0 && (
              <tr><td colSpan={6} className="admin-empty">{t.adminTemplateNoVariants}</td></tr>
            )}
          </tbody>
        </table>
        {(!products || products.length === 0) && (
          <div className="admin-editor-error" style={{ marginTop: 8 }}>
            {t.adminTemplateNoProducts} <a href="/admin/settings">{t.adminTemplateSettingsLink}</a>.
          </div>
        )}
      </div>

      {error && <div className="admin-editor-error">{error}</div>}

      <div className="admin-editor-actions">
        <button type="button" className="admin-btn-secondary" onClick={onCancel} disabled={saving}>{t.adminTemplateCancelEdit}</button>
        <button type="button" className="admin-btn-primary" onClick={save} disabled={saving}>
          {saving ? t.adminTemplateSaving : t.adminTemplateSave}
        </button>
      </div>
    </div>
  );
}

export default function AdminTemplates() {
  const t = useT();
  const [templates, setTemplates] = useState([]);
  const [products, setProducts] = useState([]);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    api.get('/api/admin/templates').then(setTemplates).catch(() => {});
    api.get('/api/admin/settings').then(s => setProducts(s.products || [])).catch(() => {});
  }, []);

  const productMap = useMemo(() => {
    const m = new Map();
    for (const p of products) m.set(p.key, p);
    return m;
  }, [products]);

  const editing = useMemo(
    () => templates.find(tp => tp._id === editingId) || null,
    [templates, editingId]
  );

  const toggleActive = async (id, isActive) => {
    await api.patch(`/api/admin/templates/${id}`, { isActive: !isActive });
    setTemplates(prev => prev.map(tp => tp._id === id ? { ...tp, isActive: !isActive } : tp));
  };

  const handleSaved = (updated) => {
    setTemplates(prev => prev.map(tp => tp._id === updated._id ? { ...tp, ...updated } : tp));
    setEditingId(null);
  };

  const minPriceLabel = (tp) => {
    const active = (tp.variants || []).filter(v => v.isActive);
    const prices = active
      .map(v => productMap.get(v.productKey))
      .filter(Boolean)
      .map(p => p.priceCents || 0);
    if (prices.length === 0) return '—';
    return `${(Math.min(...prices) / 100).toFixed(0)}${t.currency}`;
  };

  return (
    <div className="admin-page">
      <h1 className="page-title">{t.adminTemplates}</h1>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t.adminTemplateName}</th>
              <th>Slug</th>
              <th>{t.adminTemplateVariants}</th>
              <th>{t.adminTemplateFrom}</th>
              <th>{t.adminTemplateActive}</th>
              <th>{t.adminTemplateAction}</th>
            </tr>
          </thead>
          <tbody>
            {templates.map(tp => (
              <React.Fragment key={tp._id}>
                <tr>
                  <td>{tp.nameAr}</td>
                  <td className="admin-mono">{tp.slug}</td>
                  <td>{tp.variants?.length || 0}</td>
                  <td>{minPriceLabel(tp)}</td>
                  <td>
                    <button
                      className={`admin-toggle-btn ${tp.isActive ? 'active' : ''}`}
                      onClick={() => toggleActive(tp._id, tp.isActive)}
                    >
                      {tp.isActive ? t.adminTemplateActive : t.adminTemplateInactive}
                    </button>
                  </td>
                  <td>
                    <button
                      className="admin-btn-secondary"
                      onClick={() => setEditingId(editingId === tp._id ? null : tp._id)}
                    >
                      {editingId === tp._id ? t.adminTemplateClose : t.adminTemplateEdit}
                    </button>
                  </td>
                </tr>
                {editing && editing._id === tp._id && (
                  <tr className="admin-editor-row">
                    <td colSpan={6}>
                      <TemplateEditor
                        key={tp._id}
                        template={editing}
                        products={products}
                        onCancel={() => setEditingId(null)}
                        onSaved={handleSaved}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
